'use client'

import { useEnvContext } from 'next-runtime-env'

import { AxiosProvider } from '@dc-shared-web/providers/AxiosProvider'
import { ReactQueryProvider } from '@dc-shared-web/providers/ReactQueryProvider'
import { AuthProvider } from '@dc-shared-web/providers/AuthProvider'
import { APITokenProvider } from '@dc-shared-web/providers/APITokenProvider'
import { SvcCrunchesProvider } from '@dc-shared-web/providers/SvcCrunchesProvider'
import { SvcLiveblocksProvider } from '@dc-shared-web/providers/SvcLiveblocksProvider'
import { SvcCompareVersionsProvider } from '@dc-shared-web/providers/SvcCompareVersionsProvider'
import { ReactQueryDevtools } from 'react-query/devtools'

import { Toaster } from '@dc-shared-web/components/shadcn/toaster'
import { GlobalModalProvider } from '@/app/core/providers/GlobalModalProvider'
import { RollbarProvider } from '@/app/core/providers/RollbarProvider'
import CommentingRootProvider from '@/app/core/providers/CommentingRootProvider'
import IntersectionObserverProvider from '@/dc-shared-web/src/providers/IntersectionObserverContext'

import WithLDProvider from '@/app/core/providers/WithLDProvider'
import WithPylonProvider from '@/app/core/providers/WithPylonProvider'

import FSScript from '@/app/core/scripts/fullstory'
import ChameleonScript from '@/app/core/scripts/chameleon'

import GlobalModals from '@/app/core/components/GlobalModals'
import MainContent from '@/app/core/components/MainContent'

import { switzer } from '@/app/core/utils/fonts'

import '@liveblocks/react-ui/styles.css'
import '@/app/core/styles/liveblocks.css'
import { ChameleonProvider } from '@/app/core/providers/ChameleonProvider'
import { PdfInstanceProvider } from '@/app/crunches/[id]/PdfInstanceContext'
import { cn } from '@/dc-shared-web/src'
import VSSStylesScript from '@/app/core/scripts/vssStyles'

function ClientLayout({ children }: { children: React.ReactNode }) {
  const {
    NEXT_PUBLIC_DCRUNCH_API_URL,
    NEXT_PUBLIC_SVC_CRUNCHES_API_URL,
    NEXT_PUBLIC_REACT_QUERY_DEVTOOLS_ENABLED,
    NEXT_PUBLIC_SVC_LIVEBLOCKS_API_URL,
    NEXT_PUBLIC_COMPARE_API_URL,
  } = useEnvContext()

  return (
    <APITokenProvider publicURL={NEXT_PUBLIC_DCRUNCH_API_URL}>
      <AxiosProvider publicURL={NEXT_PUBLIC_DCRUNCH_API_URL}>
        <SvcCrunchesProvider publicURL={NEXT_PUBLIC_SVC_CRUNCHES_API_URL}>
          <SvcLiveblocksProvider publicURL={NEXT_PUBLIC_SVC_LIVEBLOCKS_API_URL}>
            <SvcCompareVersionsProvider publicURL={NEXT_PUBLIC_COMPARE_API_URL}>
              <RollbarProvider>
                <ReactQueryProvider>
                  <AuthProvider publicURL={NEXT_PUBLIC_DCRUNCH_API_URL}>
                    <WithLDProvider>
                      <WithPylonProvider>
                        <CommentingRootProvider>
                          <ChameleonProvider>
                            <IntersectionObserverProvider>
                              <GlobalModalProvider>
                                <PdfInstanceProvider>
                                  <html lang='en'>
                                    <head>
                                      <VSSStylesScript />
                                      <FSScript />
                                      <ChameleonScript />
                                    </head>
                                    <body
                                      className={cn(
                                        'tw-overflow-hidden',
                                        switzer.className,
                                        switzer.variable
                                      )}
                                    >
                                      <MainContent>{children}</MainContent>
                                      <Toaster position='bottom' />
                                      <GlobalModals />
                                      {process.env.NODE_ENV === 'development' &&
                                      NEXT_PUBLIC_REACT_QUERY_DEVTOOLS_ENABLED ===
                                        'true' ? (
                                        <ReactQueryDevtools
                                          initialIsOpen={false}
                                        />
                                      ) : null}
                                    </body>
                                  </html>
                                </PdfInstanceProvider>
                              </GlobalModalProvider>
                            </IntersectionObserverProvider>
                          </ChameleonProvider>
                        </CommentingRootProvider>
                      </WithPylonProvider>
                    </WithLDProvider>
                  </AuthProvider>
                </ReactQueryProvider>
              </RollbarProvider>
            </SvcCompareVersionsProvider>
          </SvcLiveblocksProvider>
        </SvcCrunchesProvider>
      </AxiosProvider>
    </APITokenProvider>
  )
}

export default ClientLayout
